<template>
	<div class="app">
		<NuxtLoadingIndicator color="var(--primary-color)" />
		<NuxtLayout>
			<NuxtPage />
		</NuxtLayout>
	</div>
</template>
<script setup lang="ts">
const { locale } = useI18n()
const currency = useCookie('currency')
const route = useRoute()
const requestURL = useRequestURL()
const newsStore = useNewsStore()
const origin = requestURL.origin
const currentUrl = computed(() => origin + route.path)
const ogLocale = computed(() => (locale.value === 'en' ? 'en_US' : 'pl_PL'))
const ogLocaleAlternate = computed(() => (locale.value === 'en' ? 'pl_PL' : 'en_US'))

if (process.client) {
	if (!currency.value) {
		currency.value = ECurrency.PLN
	}
}

if (process.client)
	window.addEventListener('vite:preloadError', () => {
		window.location.reload()
	})

const isBlogNewsTitleRoute = computed(() => (route.name?.toString() ?? '').includes('blog-newsTitle'))
const isBlogPostRoute = computed(() => isBlogNewsTitleRoute.value && newsStore.state.pageContext?.type === 'post')
const isBlogCategoryRoute = computed(() => isBlogNewsTitleRoute.value && newsStore.state.pageContext?.type === 'category')

const getNewsTranslatedSlug = (lang: 'pl' | 'en') => {
	const post = newsStore.state.pageContext?.post
	if (!post) return null
	if (post.language?.toLowerCase() === lang) return post.slug ?? null
	return post.translations?.find((translation) => translation.language.toLowerCase() === lang)?.slug ?? null
}

const getCategoryTranslatedSlug = (lang: 'pl' | 'en') => {
	const category = newsStore.state.pageContext?.type === 'category' ? newsStore.state.pageContext.category : null
	return category?.slug?.[lang] ?? null
}

const routeName = computed(() => route.name?.toString() ?? '')

const isPolishHrefLang = computed(() => {
	if (routeName.value === 'car-rental-urlSlug___en') return false
	if (isBlogPostRoute.value) return !!getNewsTranslatedSlug('pl')
	if (isBlogCategoryRoute.value) return !!getCategoryTranslatedSlug('pl')
	return true
})

const isEnglishHrefLang = computed(() => {
	if (routeName.value === 'car-rental-urlSlug___pl') return false
	if (isBlogPostRoute.value) return !!getNewsTranslatedSlug('en')
	if (isBlogCategoryRoute.value) return !!getCategoryTranslatedSlug('en')
	return true
})

const localePath = useLocalePath()
const buildHrefLang = (lang: 'pl' | 'en') => {
	const name = routeName.value
	if (name.includes('blog-newsTitle')) {
		if (isBlogPostRoute.value) {
			const translatedSlug = getNewsTranslatedSlug(lang)
			return translatedSlug ? origin + localePath({ name: 'blog-newsTitle', params: { newsTitle: [translatedSlug] } }, lang) : currentUrl.value
		}
		if (isBlogCategoryRoute.value) {
			const translatedCategorySlug = getCategoryTranslatedSlug(lang)
			if (!translatedCategorySlug) return currentUrl.value
			const page = newsStore.state.pageContext?.page ?? 1
			const newsTitle = page > 1 ? [translatedCategorySlug, page.toString()] : [translatedCategorySlug]
			return origin + localePath({ name: 'blog-newsTitle', params: { newsTitle } }, lang)
		}
		// Plain pagination (/blog/2, /blog/3, …) — the page number is language-agnostic, just swap locale.
		return origin + localePath({ name: 'blog-newsTitle', params: route.params }, lang)
	}
	if (name.includes('news-newsID-newsTitle') || name.includes('car-rental-urlSlug')) return currentUrl.value
	if (name === 'index') return !lang || lang === 'pl' ? origin : `${origin}/${lang}`
	return origin + localePath(name.slice(0, -5), lang)
}
const hrefLangLinks = computed(() => {
	const links: { key: string; rel: string; hreflang: string; href: string }[] = []
	if (isPolishHrefLang.value) links.push({ key: 'hreflang-pl', rel: 'alternate', hreflang: 'pl', href: buildHrefLang('pl') })
	if (isEnglishHrefLang.value) links.push({ key: 'hreflang-en', rel: 'alternate', hreflang: 'en', href: buildHrefLang('en') })
	links.push({ key: 'hreflang-x-default', rel: 'alternate', hreflang: 'x-default', href: buildHrefLang('pl') })
	return links
})

useHead({
	htmlAttrs: { lang: locale },
	link: hrefLangLinks,
	meta: [
		{ key: 'og-url', property: 'og:url', content: currentUrl },
		{ key: 'og-locale', property: 'og:locale', content: ogLocale },
		{ key: 'og-locale-alternate', property: 'og:locale:alternate', content: ogLocaleAlternate },
	],
})
</script>
